import React, { useContext } from 'react';
import styled from 'styled-components';

import Icon from '../../misc/Icon';
import Button from '../../misc/Button';
import InputSettingsField from './InputSettingsField';

import ThemeIcons from '../../../theme/Icons';
import ThemeContext from '../../../theme/ThemeContext';

import ConditionalLogic from '../../../constants/ConditionalLogic';
import FieldTableColumnTypes from '../../../constants/FieldTableColumnTypes';

const StyledWrapper = styled.div`
  display: flex;
  flex-direction: column;
`;

const StyledStepLabel = styled.div`
  font-weight: 500;
  margin-left: 6px;
  font-size: 14px;
  color: ${(props) => props.theme.fontColorPrimary};
`;

const StyledConditionColumn = styled.div`
  display: flex;
  flex-direction: column;
  ${(props) => (props.conditions && props.conditions.length > 1
    ? 'max-height: 450px; overflow-y: auto;'
    : '')};
`;

const StyledConditionRow = styled.div`
  display: flex;
  flex: 1;
  margin-bottom: 8px;
  flex-direction: row;
  align-items: center;
`;

const StyledConditionWrapper = styled.div`
  display: flex;
  flex-direction: row;
  flex: 1;
  padding: 8px;
  margin-bottom: 8px;
  padding-bottom: 2px;
  border-radius: ${(props) => props.theme.borderRadius}px;
  border: 1px solid ${(props) => props.theme.borderColor};
  background-color: ${(props) => props.theme.secondaryColor};
`;

const StyledConditionSelectWrapper = styled.div`
  display: flex;
`;

const StyledConditionDelete = styled.div`
  display: flex;
  flex: 1;
  align-items: center;
  justify-content: center;
  margin-left: 8px;
`;

export const CollectionConditionalLogicSettingsForm = ({
  targetLabel,
  activeSchemaTable,
  schema,
  logic,
  onChange
}) => {
  const theme = useContext(ThemeContext);
  const { parentId } = activeSchemaTable;

  const handleChange = (key, value) => {
    const nextLogic = JSON.parse(JSON.stringify(logic));
    nextLogic[key] = value;
    if (onChange) onChange(nextLogic);
  };

  const handleConditionCreate = () => {
    const nextLogic = JSON.parse(JSON.stringify(logic));
    if (nextLogic.conditions) {
      nextLogic.conditions.push({ schema: parentId });
    } else {
      nextLogic.conditions = [{ schema: parentId }];
    }
    onChange(nextLogic);
  };

  const handleConditionChange = (index, changes) => {
    const nextLogic = JSON.parse(JSON.stringify(logic));
    if (!nextLogic.conditions[index]) return;
    nextLogic.conditions[index] = {
      ...nextLogic.conditions[index],
      ...changes
    };
    onChange(nextLogic);
  };

  const handleConditionDelete = (index) => {
    const nextLogic = JSON.parse(JSON.stringify(logic));
    nextLogic.conditions.splice(index, 1);
    onChange(nextLogic);
  };

  return (
    <StyledWrapper>
      <StyledConditionRow>
        <div style={{ width: '75px' }}>
          <InputSettingsField
            type="dropdown"
            value={logic.action}
            options={[
              { _id: 'show', value: 'Show' },
              { _id: 'hide', value: 'Hide' }
            ]}
            onChange={(i, value) => handleChange('action', value)}
          />
        </div>
        <StyledStepLabel theme={theme}>
          this
          {' '}
          {targetLabel}
          {' '}
          when
        </StyledStepLabel>
      </StyledConditionRow>

      <StyledConditionRow>
        <div style={{ width: '75px' }}>
          <InputSettingsField
            type="dropdown"
            inputStyles={{ maxWidth: '100px' }}
            value={logic.eval}
            options={[
              { _id: 'and', value: 'All' },
              { _id: 'or', value: 'Any' }
            ]}
            onChange={(i, value) => handleChange('eval', value)}
          />
        </div>
        <StyledStepLabel theme={theme}>of the following conditions match</StyledStepLabel>
      </StyledConditionRow>

      <StyledConditionRow style={{ marginBottom: 0 }}>
        <StyledConditionColumn style={{ flex: 1 }} conditions={logic?.conditions}>
          {logic?.conditions?.map((condition, index) => (
            <LogicCondition
              key={index}
              index={index}
              {...condition}
              schema={schema}
              activeSchemaTable={activeSchemaTable}
              onChange={handleConditionChange}
              onDelete={handleConditionDelete}
            />
          ))}
        </StyledConditionColumn>
      </StyledConditionRow>

      <StyledConditionRow>
        <Button icon={ThemeIcons.misc.add} onClick={handleConditionCreate} />
      </StyledConditionRow>
    </StyledWrapper>
  );
};

export const LogicCondition = ({
  index,
  schema,
  activeSchemaTable,
  column,
  condition,
  value,
  onChange,
  onDelete
}) => {
  const theme = useContext(ThemeContext);

  const parentTable = schema[activeSchemaTable.parentId];

  const columnDefinitions = parentTable.tableColumns.filter((col) => [
    FieldTableColumnTypes.text,
    FieldTableColumnTypes.number,
    FieldTableColumnTypes.dropdown,
    FieldTableColumnTypes.multiSelect,
    FieldTableColumnTypes.barcode,
  ].includes(col.type));

  const parentColumnOptions = columnDefinitions.map((col) => ({
    _id: col._id,
    value: col.title || col.identifier || col._id,
  }));

  const selectedColumn = columnDefinitions.find((col) => col._id === column);

  let conditionOptions = [];
  let valueNode = null;

  if (selectedColumn) {
    switch (selectedColumn.type) {
      case FieldTableColumnTypes.text:
      case FieldTableColumnTypes.barcode:
        conditionOptions = [
          { _id: ConditionalLogic.conditions.filled, value: 'is filled' },
          { _id: ConditionalLogic.conditions.empty, value: 'is empty' },
          { _id: ConditionalLogic.conditions.equal, value: 'is' },
          { _id: ConditionalLogic.conditions.notEqual, value: 'is not' },
          { _id: ConditionalLogic.conditions.contain, value: 'contains' },
        ];
        valueNode = (
          <InputSettingsField
            label="Value"
            value={value}
            type="text"
            onChange={(i, val) => onChange(index, { value: val })}
          />
        );
        break;

      case FieldTableColumnTypes.number:
        conditionOptions = [
          { _id: ConditionalLogic.conditions.filled, value: 'is filled' },
          { _id: ConditionalLogic.conditions.empty, value: 'is empty' },
          { _id: ConditionalLogic.conditions.equal, value: 'is' },
          { _id: ConditionalLogic.conditions.notEqual, value: 'is not' },
          { _id: ConditionalLogic.conditions.greaterThan, value: 'is greater than' },
          { _id: ConditionalLogic.conditions.lessThan, value: 'is less than' },
        ];
        valueNode = (
          <InputSettingsField
            label="Value"
            value={value}
            type="number"
            onChange={(i, val) => onChange(index, { value: val })}
          />
        );
        break;

      case FieldTableColumnTypes.dropdown:
      case FieldTableColumnTypes.multiSelect: {
        conditionOptions = [
          { _id: ConditionalLogic.conditions.filled, value: 'is filled' },
          { _id: ConditionalLogic.conditions.empty, value: 'is empty' },
          { _id: ConditionalLogic.conditions.equal, value: 'is' },
          { _id: ConditionalLogic.conditions.notEqual, value: 'is not' },
        ];

        const optionItems = (selectedColumn.options || [])
          .filter((opt) => !opt.deleted)
          .map((opt) => ({ _id: opt._id, value: opt.value }));

        valueNode = (
          <InputSettingsField
            label="Value"
            value={value}
            type="dropdown"
            options={optionItems}
            onChange={(i, val) => onChange(index, { value: val })}
          />
        );
        break;
      }

      default:
        break;
    }
  }

  const showValueField =
    selectedColumn &&
    ![ConditionalLogic.conditions.filled, ConditionalLogic.conditions.empty].includes(condition);

  return (
    <StyledConditionWrapper theme={theme}>
      <StyledConditionColumn style={{ flex: 1 }}>
        <StyledConditionRow>
          <InputSettingsField
            label="Column"
            value={column}
            type="dropdown"
            options={parentColumnOptions}
            onChange={(i, columnId) => onChange(index, { column: columnId })}
          />
        </StyledConditionRow>

        {selectedColumn && (
          <StyledConditionRow>
            <InputSettingsField
              label="Condition"
              value={condition}
              type="dropdown"
              options={conditionOptions}
              onChange={(i, val) => onChange(index, { condition: val })}
            />
          </StyledConditionRow>
        )}

        {selectedColumn && condition && showValueField && (
          <StyledConditionRow>
            <StyledConditionSelectWrapper>{valueNode}</StyledConditionSelectWrapper>
          </StyledConditionRow>
        )}
      </StyledConditionColumn>

      <StyledConditionColumn>
        <StyledConditionDelete>
          <Icon
            color={theme.dangerColor}
            icon={ThemeIcons.settings.delete}
            size="sm"
            onClick={() => onDelete(index)}
          />
        </StyledConditionDelete>
      </StyledConditionColumn>
    </StyledConditionWrapper>
  );
};

export default CollectionConditionalLogicSettingsForm;
